import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import { Host } from '../../../Host';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ClipDataBuilder, ClipboardService } from '../../../platform/Clipboard';
import { DropMenuButton } from '../../../ui/MenuButton';
import { useResolve } from '../../Container';
import { usePermissionCheck } from '../../common/PermissionElements';
import { boardClone, boardDelete, boardMove, boardRename, boardSetPinned } from '../../workspace/BoardCommands';
import { Menu } from './../../../ui';
import { Flexer } from './LayoutElements';

export type BoardActionMenuConfig = boolean | Record<string, boolean>;
export type BoardActionMenuProps = {
    config?: BoardActionMenuConfig,
    board: { id: string, pinned?: boolean },
    onLayoutAffected?: VoidCallback,
};


export const BoardActionMenu = React.memo<BoardActionMenuProps>(({ config, board, onLayoutAffected }) => {
    onLayoutAffected = onLayoutAffected ?? (() => { });
    config = config === undefined ? {} : config;

    const clipboard = useResolve(ClipboardService);
    const dispatch = useResolve(CommandDispatcher);
    const [open, setOpen] = useState(false);
    const canEdit = usePermissionCheck('da:write', 'wo:write');

    const onCopyLink = useCallback(() => {
        const data = new ClipDataBuilder();
        data.setText(Host.absUrl(board.id));
        clipboard.write(data.build());
    }, [board.id]);

    const onPinOrUnpin = useCallback(async () => {
        await dispatch(boardSetPinned, [board.id, !board.pinned]);
        onLayoutAffected();
    }, [board.id, board.pinned, onLayoutAffected]);

    const close = () => {
        setOpen(false);
        return true;
    };

    const check = useCallback((action: string) => {
        if (config === true) return true;
        if (config[action] !== false) return true;
        return false;
    }, [config]);

    if (config === false) return null;

    return <Flexer margin="0 .5">
        <DropMenuButton icon="deqo://icons/hero/dots-horizontal" open={open} setOpen={setOpen}>
            <Flexer minWidth="10">
                <Menu>
                    {canEdit && check('pin') && <>
                        <Menu.Item onClick={() => close() && onPinOrUnpin()}>
                            {board.pinned ? 'Unpin' : 'Pin'}
                        </Menu.Item>
                    </>}
                    <Menu.Item onClick={() => close() && onCopyLink()}>
                        <Menu.ItemText>Copy Link</Menu.ItemText>
                    </Menu.Item>
                    {canEdit && <>
                        <Menu.Divide />
                        {check('rename') && <Menu.Item onClick={() => close() && dispatch(boardRename, [board.id])}>
                            <Menu.ItemText>Rename</Menu.ItemText>
                        </Menu.Item>}
                        {check('move') && <Menu.Item onClick={() => close() && dispatch(boardMove, [board.id])}>
                            <Menu.ItemText>Move</Menu.ItemText>
                        </Menu.Item>}
                        {check('clone') && <Menu.Item onClick={() => close() && dispatch(boardClone, [board.id])}>
                            <Menu.ItemText>Clone</Menu.ItemText>
                        </Menu.Item>}
                        {check('delete') && <Menu.Item onClick={() => close() && dispatch(boardDelete, [board.id])}>
                            <Menu.ItemText>Delete</Menu.ItemText>
                        </Menu.Item>}
                    </>}
                </Menu>
            </Flexer>
        </DropMenuButton>
    </Flexer>;

});
